// Certificaattypes (batch-6 voertuig §3) — gedeeld request-schema voor
// POST/PATCH /certificate-types. De API parseert ermee én de "Nieuw
// certificaattype"-modal valideert er client-side mee vóór verzenden, zodat
// het form niets kan sturen dat de API weigert (CLAUDE.md §Validatie).

import { z } from 'zod'
import { CERTIFICAAT_GROEPEN, type CertificaatGroep } from './verplichte-certificaten'

export interface CertificateTypeDTO {
  id: string
  name: string
  code: string
  description: string | null
  validityMonths: number | null
  isActive: boolean
  /** batch-71 (FO-541): de groep van de verplichte eis waar dit type aan voldoet. */
  certificaatGroep?: CertificaatGroep | null
  createdAt: string
  updatedAt: string
  deletedAt: string | null
}

export const CreateCertificateTypeSchema = z.object({
  name: z.string().trim().min(1, 'Naam is verplicht').max(200, 'Maximaal 200 tekens'),
  code: z
    .string()
    .trim()
    .toUpperCase()
    .min(1, 'Code is verplicht')
    .max(50, 'Maximaal 50 tekens')
    .regex(/^[A-Z0-9_]+$/, 'Alleen hoofdletters, cijfers en underscores'),
  description: z.string().trim().max(1000, 'Maximaal 1000 tekens').optional(),
  // Leeg/null = certificaat verloopt niet. `.nullable()` zodat de read-DTO
  // (validityMonths: null) round-tript zonder VALIDATION_ERROR.
  validityMonths: z
    .number()
    .int('Geheel aantal maanden')
    .positive('Minimaal 1 maand')
    .max(1200, 'Maximaal 1200 maanden')
    .nullable()
    .optional(),
  isActive: z.boolean().optional(),
  // batch-71 verplichte-certificaten (FO-541): een nieuwe variant hoort meteen bij zijn groep, anders
  // voldoet hij niet aan de groep-eis (VCA, NEN 3140, hoogwerker, heftruck, BHV). Null = geen groep.
  certificaatGroep: z.enum(CERTIFICAAT_GROEPEN).nullable().optional(),
})
export type CreateCertificateTypeInput = z.infer<typeof CreateCertificateTypeSchema>

export const UpdateCertificateTypeSchema = CreateCertificateTypeSchema.partial()
export type UpdateCertificateTypeInput = z.infer<typeof UpdateCertificateTypeSchema>

/**
 * Auto-suggestie voor het code-veld op basis van de naam: accenten strippen,
 * hoofdletters, niet-alfanumeriek → underscore. Spiegelt de bestaande
 * seed-conventie (bv. "NEN 3140 VP - Vakbekwaam persoon" →
 * `NEN_3140_VP_VAKBEKWAAM_PERSOON`; de gebruiker kan inkorten).
 */
export function suggestCertificateTypeCode(name: string): string {
  return name
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 50)
}
